import { nsDfs } from '@qdabra/forms-shared/xmlNamespaces';
import { MAIN_DATASOURCE_NAME } from '../../../constants/dataSource.constants';
import {
    createDocument,
    makeXPathEngineFromNamespaces,
} from '../../../utility/xmlHelpers';
import { getNodeValue } from '../../../xmlUtility';
import {
    paramDocId,
    paramGetDocument,
    paramUpgradeDocument,
    paramVersion,
} from '../constants/parameterNames';
import { typeBoolean, typeDataSource } from '../constants/parameterTypes';
import { NS_QDABRA_WEBSERVICE } from '../helpers/dbxl.helpers';
import getDocumentWrapper from '../helpers/getDocumentWrapper';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataConnection } from '../helpers/qRules.dataConnection.helpers';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { checkValidNode } from '../helpers/qRules.node.helpers';
import { setDbxlPiValues } from '../helpers/qRules.processingInstruction.helpers';
import {
    selectNodeFromXPathEngine,
    selectNodeListFromXPathEngine,
} from '../helpers/qRules.utility';
import { swapDomContents } from '../helpers/swapDomHelpers';

const optionalParameters = [
    {
        name: paramUpgradeDocument,
        description:
            'It attempts to apply the template’s built in upgrade to the incoming document',
        type: typeBoolean,
    },
    {
        name: paramVersion,
        description:
            'Allows querying a previous version of a form stored in DBXL',
    },
];

const requiredParameters = [
    {
        name: paramGetDocument,
        description: 'DBXL GetDocument data connection name',
        type: typeDataSource,
    },
    {
        name: paramDocId,
        description: 'Document ID to replace the DOM with',
    },
];
const invalidDocIdFormat = 'Invalid document id: {0}';
const description =
    'After retrieving documents by passing in the DBXL document ID in the input parameters, this command replaces the main DOM with the retrieved XML.';

const createXPathEngine = () =>
    makeXPathEngineFromNamespaces({
        QDW: NS_QDABRA_WEBSERVICE,
        dfs: nsDfs,
    });

function buildErrorList(xPathEngine, resultNode) {
    const errorNodes = selectNodeListFromXPathEngine(
        xPathEngine,
        resultNode,
        'QDW:Errors/QDW:ErrorInfo',
    );

    if (!errorNodes || errorNodes.length === 0) {
        return 'No specific errors were returned.';
    }

    let errors = '';

    errorNodes.forEach(function (errorNode) {
        const code = selectNodeFromXPathEngine(
            xPathEngine,
            'QDW:Code',
            errorNode,
        );
        const error = selectNodeFromXPathEngine(
            xPathEngine,
            'QDW:Description',
            errorNode,
        );

        errors += String.format(
            '{0}: {1}\r\n',
            getNodeValue(code),
            getNodeValue(error),
        );
    });

    return errors;
}

async function getDocument(xPathEngine, docid, wrapper, version) {
    wrapper.docId(docid);

    //If version is included, we will assume a GetDocumentVersion data connection
    if (version) {
        wrapper.version(version);
    }

    await wrapper.executeAsync();

    const resultNode = checkValidNode(
        wrapper.resultNode(),
        'Unable to locate GetDocumentResult node',
    );
    const successNode = checkValidNode(
        wrapper.successNode(),
        'Unable to locate Success node',
    );

    if (getNodeValue(successNode) !== 'true') {
        // TODO: fix buildErrorList
        throw new Error(buildErrorList(xPathEngine, resultNode));
    }
}

/**
 * Upgrades the provided node using the template's upgrade settings, if they are specified
 * @param newDoc - The XML element representing the root element of the document to upgrade
 * @returns the root element of the upgraded document, or newDoc if no upgrade settings are specified
 */
// TODO: Consolidate with upgrade functionality called from loader.js
async function tryToUpgradeAsync(transforms, template, newDoc) {
    // Case 40517 Implement Upgrade
    try {
        const info = template.getUpgradeSettings();

        const isTransformUpgrade = info?.type === 'transform';

        if (!isTransformUpgrade) {
            return newDoc;
        }

        const inputDoc = createDocument(newDoc);

        const transformed = await transforms.transform(
            info.transform,
            inputDoc,
        );

        const docEl = transformed?.documentElement;

        if (!docEl) {
            throw new Error(
                'Unable to locate root element of upgraded document.',
            );
        }

        return docEl;
    } catch (e) {
        throw new Error(
            `An error occurred trying to upgrade the document: ${e.message}`,
            e,
        );
    }
}

const replaceDom = async (
    wrapper,
    template,
    dataSources,
    transforms,
    upgradeDocument,
) => {
    checkValidNode(wrapper.contentNode(), 'Unable to find Content element');
    const newDoc = checkValidNode(
        wrapper.documentRoot(),
        'Unable to locate contents of document to swap',
    );

    try {
        const docToInsert = upgradeDocument
            ? await tryToUpgradeAsync(transforms, template, newDoc)
            : newDoc;

        await swapDomContents(dataSources, MAIN_DATASOURCE_NAME, docToInsert);
    } catch (e) {
        console.error('Error swapping DOM', e);

        throw new Error('Errow swapping DOM: ' + e.message);
    }
};

function setFormPi(docIdInt, wrapper, mainDom) {
    setDbxlPiValues(
        mainDom.getNode(),
        docIdInt,
        '', // doctype
        wrapper.name(),
        wrapper.author(),
        wrapper.description(),
    );
}

async function executeAsync({
    dataConnections,
    dataSources,
    parameters,
    template,
    transforms,
}) {
    const getDocumentConnectionName = getParamValue(
        parameters,
        paramGetDocument,
    );
    const docid = getParamValue(parameters, paramDocId);
    const version = getParamValue(parameters, paramVersion);
    const upgrade = getBoolParamValue(parameters, paramUpgradeDocument);
    const docIdInt = parseInt(docid);

    if (Number.isNaN(docIdInt)) {
        throw new Error(String.format(invalidDocIdFormat, docid));
    }

    const xPathEngine = createXPathEngine();

    const mainDom = getDataSource(dataSources, '');

    const getDocumentDs = getDataSource(dataSources, getDocumentConnectionName);
    const getDocumentConn = getDataConnection(
        dataConnections,
        getDocumentConnectionName,
    );
    const wrapper = getDocumentWrapper(getDocumentDs, getDocumentConn);

    await getDocument(xPathEngine, docid, wrapper, version);

    await replaceDom(wrapper, template, dataSources, transforms, upgrade);

    setFormPi(docIdInt, wrapper, mainDom);

    return '';
}

const SwapDomWithDocument = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default SwapDomWithDocument;
